import { ReactNode } from 'react';
import {
  AttributionKey,
  DataTableQuerySettingKey,
  DateKindKey,
  ExpressionModeKey,
  ExpressionQuerySettingKey,
  FunnelModeKey,
  FunnelOrderKey,
  FunnelQuerySettingKey,
  GroupbyKey,
  PathModeKey,
  PathQuerySettingKey,
  RetentionCalculationKey,
  RetentionCalculationWindowKey,
  RetentionQuerySettingKey,
  RetentionViewKey,
  RollingControlFunction,
  TimeWindowKey,
} from '../../utils/apollo/graphqlSchema';
import { UnreachableCaseError } from 'ts-essentials';
import {
  CANT_DELETE_NON_EMPTY_FOLDER,
  PERMISSION_ERROR_MESSAGE,
} from '../Chart/constants/utils';
import { MaybeNil } from '../../utils/interfaces';

export type ITooltipOption = {
  content?: ReactNode;
  actionText?: string;
  actionUrl?: string;
  disabled?: boolean;
};

const defaultPermissionTooltipOption: ITooltipOption = {
  content: PERMISSION_ERROR_MESSAGE,
};

const cantDeleteNonEmptyFolderTooltipOption: ITooltipOption = {
  content: CANT_DELETE_NON_EMPTY_FOLDER,
};

const funnelOrderTooltips: Record<FunnelOrderKey, ITooltipOption> = {
  [FunnelOrderKey.Any]: {
    content: `Events can happen in any order.`,
    actionText: 'Learn More',
    actionUrl: `https://docs.kubit.ai/docs/funnel#any-order`,
  },
  [FunnelOrderKey.Sequence]: {
    content: `Events happen in this order.`,
    actionText: 'Learn More',
    actionUrl: `https://docs.kubit.ai/docs/funnel#sequence-order`,
  },
  [FunnelOrderKey.Exact]: {
    content: `Only these events have to happen in this specific order.`,
    actionText: 'Learn More',
    actionUrl: `https://docs.kubit.ai/docs/funnel#exact-order`,
  },
  [FunnelOrderKey.Custom]: {
    content: `Create a custom order of events.`,
    actionText: 'Learn More',
    actionUrl: `https://docs.kubit.ai/docs/funnel#custom-order`,
  },
};

const groupbyTooltips: Record<GroupbyKey, ITooltipOption> = {
  [GroupbyKey.Starting]: {
    content: `Breakdown applied over the first step,
    subsequent steps do not have to fall within the same breakdown group.`,
  },
  [GroupbyKey.Global]: {
    content: `All the steps have to happen within the same breakdown group.`,
  },
};

const funnelModeTooltips: Record<FunnelModeKey, ITooltipOption> = {
  [FunnelModeKey.Conversion]: {
    content: `How do users convert?`,
  },
  [FunnelModeKey.Frequency]: {
    content: `How many times users generated an event before they converted to the next step?`,
  },
  [FunnelModeKey.TimeToConvert]: {
    content: `How much time does it take users to convert between steps?`,
  },
};

const attributionTooltips: Record<AttributionKey, ITooltipOption> = {
  [AttributionKey.Linear]: {
    content: `Linear means that a User will be assigned to
    all Cohorts when the Starting Event(s) occurs (inclusive)`,
  },
  [AttributionKey.First]: {
    content: `First means that a User will be assigned only to the Cohort
    for the day when the first Starting Event(s) occurred.`,
  },
};

const retentionCalculationWindowTooltips: Record<
  RetentionCalculationWindowKey,
  ITooltipOption
> = {
  [RetentionCalculationWindowKey.StrictCalendarDate]: {
    content: `Each day is a calendar date.`,
  },
  [RetentionCalculationWindowKey.Rolling_24Hours]: {
    content: `Each day is a 24-hour window.`,
  },
};

const retentionCalculationTooltips: Record<
  RetentionCalculationKey,
  ITooltipOption
> = {
  [RetentionCalculationKey.Normal]: {
    content: `Users who come back and fire the return event on a specific day (or W/M/Y/A).`,
  },
  [RetentionCalculationKey.Rolling]: {
    content: `Users who come back based on the previous day (or W/M/Y/A).`,
  },
  [RetentionCalculationKey.Unbounded]: {
    content: `Users who fire the return event on a specific day (or W/M/Y/A) or later.`,
  },
};

const funnelModeTooltipOption: ITooltipOption = {
  content:
    'Answer specific questions about conversion, frequency, and time to convert.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/funnel#mode',
};

const funnelOrderTooltipOption: ITooltipOption = {
  content: 'Customize the order and rules between steps',
  actionUrl: 'https://docs.kubit.ai/docs/funnel#sequence-order-default',
};

const pathGroupByTooltipOption: ITooltipOption = {
  content: 'Only applicable when there is a breakdown.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/path#group-by',
};

const funnelGroupByTooltipOption: ITooltipOption = {
  content: 'Only applicable when there is a breakdown.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/funnel#group-by',
};

const retentionGroupByTooltipOption: ITooltipOption = {
  content: 'Only applicable when there is a breakdown.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/retention#breakdown-in-retention',
};

const pathModeTooltipOption: ITooltipOption = {
  content:
    'Answer specific questions about pathing analysis like overall flows vs. top flows.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/path',
};

const pathModeTooltips: Record<PathModeKey, ITooltipOption> = {
  [PathModeKey.Sankey]: {
    content: 'An aggregate level flow diagram.',
  },
  [PathModeKey.TopPaths]: {
    content: 'A specific view into the top 10 paths between two events.',
  },
};

export const dateKindTooltip = (
  dateKindKey: DateKindKey,
  name: string,
): ITooltipOption => {
  switch (dateKindKey) {
    case DateKindKey.Event:
    case DateKindKey.Install:
      return {
        content: `Select by ${name} date.`,
      };
    case DateKindKey.Trigger:
      return {
        content:
          'Show the impact of a Trigger Event before and after the Trigger Date.',
        actionUrl:
          'https://docs.kubit.ai/docs/advanced-query-features#impact-analysis',
      };
    default:
      throw new UnreachableCaseError(dateKindKey);
  }
};

const retentionAttributionTooltipOption: ITooltipOption = {
  content: 'Select different attribution modes.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/retention#attribution',
};

const retentionModeTooltipOption: ITooltipOption = {
  content: 'Choose which retention mode to apply.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/retention#retention-modes',
};

const retentionModeTooltips: Record<RetentionViewKey, ITooltipOption> = {
  [RetentionViewKey.Retention]: {
    content: `Retention shows how different Cohorts of users
    (defined by Starting Events on a Date) retain over time.`,
  },
  [RetentionViewKey.RetentionOverTime]: {
    content: `Retention Over Time shows the % of users who are part of the starting cohort
    and have at least one returning event on that date.`,
  },
  [RetentionViewKey.UsageInterval]: {
    content: `Usage Interval shows  the typical Usage Interval of a product feature,
    e.g what % of users have generated the returning event by Day X.`,
  },
  [RetentionViewKey.UsageIntervalOverTime]: {
    content: `Usage Interval Over Time shows you how the
    Usage Interval of an event changes over time.`,
  },
};

const cohortConditionFilterTooltipOption: ITooltipOption = {
  content: `Filter users based on actions they performed,
  saved measures or reference previously created analysis charts.`,
  actionText: 'Learn More',
  actionUrl: `https://docs.kubit.ai/docs/cohorts#type`,
};

const cohortTimeWindowTooltips: Record<TimeWindowKey, ITooltipOption> = {
  [TimeWindowKey.Current]: {
    content: `A shortcut for Rolling backward 0 days.`,
  },
  [TimeWindowKey.Overall]: {
    content: `For the defined date range of the analysis or the static date range of the Cohort Sample.`,
  },
  [TimeWindowKey.RollingBack]: {
    content: `Subjects are selected as long as they satisfied the Cohort
      conditions in the last X days for any of the dates in the date range.`,
  },
  [TimeWindowKey.RollingBackBetween]: {
    content: `Subjects are selected as long as they satisfied the
    Cohort conditions between X and Y days before any of the dates in the date range.`,
  },
  [TimeWindowKey.RollingForward]: {
    content: `Opposite of Rolling backward.`,
  },
  [TimeWindowKey.RollingForwardBetween]: {
    content: `Opposite of Rolling backward between.`,
  },
  [TimeWindowKey.Within]: {
    content: ``,
  },
  [TimeWindowKey.BetweenSinceStart]: {
    content: ``,
  },
  [TimeWindowKey.InEach]: {
    content: ``,
  },
};

const subjectTooltipOption: ITooltipOption = {
  content: `Provides the option of analyzing by different subjects.`,
};

const funnelPartitionTooltipOption: ITooltipOption = {
  content: `Apply time constraints on conversion timing.`,
};

const pathPartitionTooltipOption: ITooltipOption = {
  content: `Apply time constraints on conversion timing.`,
  actionText: 'Learn More',
  actionUrl: `https://docs.kubit.ai/docs/path#partition`,
};

const breakdownOverallTooltipOption: ITooltipOption = {
  content:
    `Use Overall to display the cumulative count for all breakdown groups. ` +
    `If not toggled, overall will be the sum of all returned breakdown groups.`,
  actionText: 'Learn More',
  actionUrl: `https://docs.kubit.ai/docs/query#overall-in-query`,
};

const cohortSampleTooltipOption: ITooltipOption = {
  content: `Create and save a user segment for use in analytical reports.`,
  actionText: 'Learn More',
  actionUrl: `https://docs.kubit.ai/docs/cohorts#what-is-a-cohort`,
};

const cohortCompareTooltipOption: ITooltipOption = {
  content: `Create and save the common users between two cohorts.`,
  actionText: 'Learn More',
  actionUrl: `https://docs.kubit.ai/docs/cohorts#compare-cohorts`,
};

const dashboardFolderTooltipOption: ITooltipOption = {
  content: `Create a new dashboard folder for easy management & organization.`,
  actionText: 'Learn More',
  actionUrl: `https://docs.kubit.ai/docs/creating-dashboards`,
};

const saveMeasureTooltipOption: ITooltipOption = {
  content:
    'Save a custom measure when it is completely defined. ' +
    'All Saved Measures will be available in the Measure dropdown list for reuse.',
  actionText: 'Learn More',
  actionUrl:
    'https://docs.kubit.ai/docs/components-of-a-report#saving-and-updating-measures',
};

const anomalyDetectionInfoTooltipOption: ITooltipOption = {
  content:
    'A machine learning-based algorithm will be employed, ' +
    'incorporating a variety of forecasting techniques such as LSTM, GESD, LOWESS, STL, ' +
    'and others, to automatically identify anomalies within the time series data.',
};

const brazeRemoveOutdatedTooltipOption: ITooltipOption = {
  content:
    'When enabled, any users imported in the previous run which are no longer part of the Cohort ' +
    'will be removed from the Braze database. This option can not be changed once the schedule is created',
};

const samplingRateTooltipOption: ITooltipOption = {
  content: 'Sampling Rate %.',
};

const expressionModeTooltip: ITooltipOption = {
  content:
    'Answer specific questions related to "How Many" users are performing specific events within your product',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/query#query-use-cases',
};

const expressionModeOptionTooltips: Record<ExpressionModeKey, ITooltipOption> =
  {
    [ExpressionModeKey.Query]: {
      content: `"How Many" users are performing specific events within your product.`,
    },
    [ExpressionModeKey.Impact]: {
      content:
        'How users engage with a feature impacts how they behave over time.',
    },
    [ExpressionModeKey.Histogram]: {
      content:
        'Group your subject data into intervals based on the frequency of occurrences within each bin.',
    },
  };

const expressionAdditionalSettingsTooltips: Record<
  ExpressionQuerySettingKey,
  ITooltipOption
> = {
  [ExpressionQuerySettingKey.Sampling]: samplingRateTooltipOption,
};

const funnelAdditionalSettingsTooltips: Record<
  FunnelQuerySettingKey,
  ITooltipOption
> = {
  [FunnelQuerySettingKey.GroupBy]: funnelGroupByTooltipOption,
  [FunnelQuerySettingKey.Partition]: funnelPartitionTooltipOption,
  [FunnelQuerySettingKey.Sampling]: samplingRateTooltipOption,
};

const pathAdditionalSettingsTooltips: Record<
  PathQuerySettingKey,
  ITooltipOption
> = {
  [PathQuerySettingKey.GroupBy]: pathGroupByTooltipOption,
  [PathQuerySettingKey.Partition]: pathPartitionTooltipOption,
  [PathQuerySettingKey.Sampling]: samplingRateTooltipOption,
};

const retentionAdditionalSettingsTooltips: Record<
  RetentionQuerySettingKey,
  ITooltipOption
> = {
  [RetentionQuerySettingKey.Attribution]: retentionAttributionTooltipOption,
  [RetentionQuerySettingKey.GroupBy]: retentionGroupByTooltipOption,
  [RetentionQuerySettingKey.Sampling]: samplingRateTooltipOption,
};

const dataTableAdditionalSettingsTooltips: Record<
  DataTableQuerySettingKey,
  ITooltipOption
> = {
  [DataTableQuerySettingKey.Sampling]: samplingRateTooltipOption,
};

const saveNamedFilterTooltip: ITooltipOption = {
  content: 'Save & Reuse custom Named Filters.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/components-of-a-report#filters',
};

const addQueryMeasureTooltip: ITooltipOption = {
  content: 'Create a compound measure by adding an additional measure.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/query#compound-measures',
};

const pathEventsTooltip: ITooltipOption = {
  content:
    'Identify the critical Starting and Ending events you want to analyze in your path.',
  actionText: 'Learn More',
  actionUrl:
    'https://docs.kubit.ai/docs/path#starting-events-and-ending-events',
};

const retentionBuilderCriteriaTooltip: ITooltipOption = {
  content: `Select the Cohort of users who performed the "Starting Event" during a specific period of time.`,
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/retention#introduction',
};

const retentionBuilderEventsTooltip: ITooltipOption = {
  content:
    'Identify the "Starting Event(s)" and "Returning Event(s)" that ' +
    'mark the beginning and subsequent returning actions of users.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/retention#introduction',
};

const retentionCalculationKeyTooltip: ITooltipOption = {
  content: 'Apply specific retention methods.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/retention#retention-types',
};

const pathAdjustmentsTooltip: ITooltipOption = {
  content:
    'Expose or transform events and properties to get a more meaningful Path.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/path#merge-expand-and-fold-events',
};

const expandReroutingEventTooltip: ITooltipOption = {
  content:
    'Expand an event with its field value to be treated as separate steps, eg Screen[$Name], View[$Page].',
};

const foldReroutingEventTooltip: ITooltipOption = {
  content: 'Fold multiple steps together (horizontally).',
};

const mergeReroutingEventTooltip: ITooltipOption = {
  content: 'Merge several events within a certain step into one (vertically).',
};

const queryMeasuresInfo: ITooltipOption = {
  content: 'Analyze specific event(s) and/or Saved Measures.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/query#building-your-measures',
};

const queryRollingControlFunctionsInfo: Record<
  RollingControlFunction,
  ITooltipOption
> = {
  [RollingControlFunction.Avg]: {
    content:
      'For every day of the date range do an average of the measure values for the last ' +
      'X days (including the current day)',
    actionText: 'Learn More',
    actionUrl:
      'https://docs.kubit.ai/docs/advanced-query-features#rolling-average',
  },
  [RollingControlFunction.Sum]: {
    content:
      'For every day of the date range apply the Measure Function and do a sum of the Measure values for the ' +
      'last X days (including the current day)',
    actionText: 'Learn More',
    actionUrl: 'https://docs.kubit.ai/docs/advanced-query-features#rolling-sum',
  },
  [RollingControlFunction.Window]: {
    content:
      'For every day of the date range apply the Measure Function over the date range window',
    actionText: 'Learn More',
    actionUrl:
      'https://docs.kubit.ai/docs/advanced-query-features#rolling-window',
  },
};

const queryImpactAnalysisInfo: ITooltipOption = {
  content:
    'Identify the impact of a particular metric before and after a user performs an event for the first time.',
  actionText: 'Learn More',
  actionUrl:
    'https://docs.kubit.ai/docs/advanced-query-features#impact-analysis',
};

const queryHistogramInfo: ITooltipOption = {
  content:
    'Group your subject data into intervals based on the frequency of occurrences within each bin.',
};

const queryComparisonConfigInfo: ITooltipOption = {
  content: 'Compare measure(s) current period to a past period',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/query#compare-mode-in-query',
};

const queryFormulaInfo: ITooltipOption = {
  content: 'Create and save measures using custom calculations',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/query#formula-operators',
};

const funnelStepsInfo: ITooltipOption = {
  content: 'Analyze how users progress through various steps in a funnel.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/funnel#funnel-steps',
};

const dataTableMeasureInfo: ITooltipOption = {
  content: 'Analyze specific event(s) and/or Saved Measures.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/data-table#building-a-data-table',
};

const dataModelTooltipOptions = {
  discardChangesButton: {
    content: 'Discard schema changes',
  },
  confirmChangesButton: {
    content: 'Confirm and publish schema changes',
  },
  addedTableNonRemovableIcon: {
    content:
      'New Table. You can not remove all Fact tables after a Dimension is added.',
  },
  addedTableIcon: {
    content: 'New Table. Click to remove.',
  },
  addJoinConditionIcon: {
    content: 'Add Condition',
  },
  removeJoinConditionIcon: {
    content: 'Remove Condition',
  },
  removeTemporalJoinConditionIcon: {
    content: 'Remove Temporal Condition',
  },
  modifiedTableIcon: {
    content: 'Modified Table',
  },
  addedFieldIcon: {
    content: 'New Field',
  },
  modifiedFieldIcon: {
    content: 'Modified Field',
  },
  filterCheckboxTooltipContent: {
    content: 'Enable to include this field in the Filter fields list',
  },
  groupbyCheckboxTooltipContent: {
    content: 'Enable to include this field in the Breakdown fields list',
  },
  groupbyCheckboxTooltipDisabledContent: {
    content:
      'Derived Fields of type Array can not be used in Breakdown. Please use Derived Columns instead.',
  },
  disableActionTooltipContent: {
    enabled: {
      content: 'Disable this field',
    },
    disabled: {
      content: 'Enable this field',
    },
    prevented: {
      content: 'This field is required and can not be disabled',
    },
  },
  cancelManualInputColumn: {
    content: 'Cancel Manual Input',
  },
  addManualInputColumn: {
    content: 'Add Column',
  },
};

const dataModelLabelInfoTooltipOptions = {
  maxSchemasReached: {
    content: (maxSchemas: MaybeNil<number>) => (
      <>
        Max Schemas {maxSchemas} reached. Please disable a schema or contact
        Kubit to increase your schemas
      </>
    ),
  },
  maxSchemasExceeded: {
    content: (disableCount: MaybeNil<number>) => (
      <>
        Your Max Schema Limit has been exceeded, please disable {disableCount}{' '}
        schema(s) before creating a new schema
      </>
    ),
  },
  schemaName: {
    content:
      'Provide a clear, user-friendly name to be used as the display name in the schema drop-down.',
  },
  schemaDescription: {
    content: 'Provide an optional description for this schema.',
  },
  schemaKey: {
    content: 'Auto-generated unique identifier of the Schema',
  },
  schemaEnabled: {
    content:
      'Toggle to disable or enable this schema. ' +
      'Disabled schemas are not visible in the Schema selection options.',
  },
  timezone: {
    content:
      'Identify the timezone used to define the day for daily report results',
  },
  weekStart: {
    content:
      'When aggregating weekly report results, which day is used as the ' +
      'start of the week—typically Monday or Sunday',
  },
  subjectsReadOnly: {
    content:
      'List of Subjects configured for this Schema. Additional subjects can be added from the Tables tab.',
    actionText: 'Learn More',
    actionUrl: 'https://docs.kubit.ai/docs/components-of-a-report#subject',
  },
  defaultSamplingRate: {
    content:
      'The Default Sampling Rate is applied on the report level and ' +
      'can be overriden as needed.',
    actionText: 'Learn More',
    actionUrl:
      'https://docs.kubit.ai/docs/sampling-options#how-to-sample-in-kubit',
  },
  cohortSamplingRate: {
    content:
      'Default Sampling Rate applied to all Cohorts, Cohort Filters ' +
      'and Segments, which can be overriden in reports.',
    actionText: 'Learn More',
    actionUrl:
      'https://docs.kubit.ai/docs/sampling-options#sampling-in-cohorts',
  },
  minimumRefreshInterval: {
    content: 'Minimum interval between field value refresh requests',
  },
  maximumValueLength: {
    content: 'Maximal length of field values to be included in refresh',
  },
  lookBackSeconds: {
    content: 'How many seconds to look back for field values',
  },
  addFunnelPartition: {
    content:
      'Partition Fields can be used in Funnel and Path analysis to measure conversions ' +
      'based on an identifier (e.g. session_id) rather than a time window. ',
    actionText: 'Learn More',
    actionUrl: 'https://docs.kubit.ai/docs/funnel#partition-conversion-window',
  },
  joinConditionTitle: {
    content:
      'Specify how your dimension table should join with the configured fact table(s)',
  },
  subjectUnderTable: {
    content:
      'Add a Subject in this table by specifying the name and selecting the corresponding column.',
    actionText: 'Learn More',
    actionUrl: 'https://docs.kubit.ai/docs/components-of-a-report#subject',
  },
  derivedColumn: {
    content: 'Create a new logical column using existing data.',
    actionText: 'Learn More',
    actionUrl:
      'https://docs.kubit.ai/docs/introduction-to-data-modeler#create-a-derived-column',
  },
  derivedColumnEdit: {
    content: 'Edit a logical column definition.',
    actionText: 'Learn More',
    actionUrl:
      'https://docs.kubit.ai/docs/introduction-to-data-modeler#create-a-derived-column',
  },
  connectionWarehouseType: {
    content:
      'Which data warehouse vendor do you use for storing your analytical data?',
  },
  connectionAuthenticationType: {
    content: 'Which authentication type do you use for authentication?',
  },
  connectionKeyPairPassword: {
    content: 'Password for your key',
  },
  connectionKeyPairKey: {
    content: 'Private key used to ',
    actionText: 'connect to Snowflake.',
    actionUrl: 'https://docs.snowflake.com/en/user-guide/key-pair-auth',
  },
  connectionUsername: {
    content: 'Username for the JDBC connection setup',
  },
  connectionPassword: {
    content: 'Password for the JDBC connection setup',
  },
  connectionJdbcUrl: {
    content: 'JDBC URL for the database connection.',
  },
  connectionTimeout: {
    content: 'Query Timeout configuration for the JDBC connection.',
  },
  connectionBqAccount: {
    content:
      'The email address of the Big Query Service Account used to establish the JDBC connection',
    actionText: 'Learn More',
    actionUrl: 'https://cloud.google.com/iam/docs/service-account-overview',
  },
  connectionBqDataset: {
    content:
      'Default starting location when adding new tables from the Tables tab',
  },
  connectionBqProjectId: {
    content: 'Which Big Query project contains your data?',
    actionText: 'Learn More',
    actionUrl:
      'https://cloud.google.com/resource-manager/docs/creating-managing-projects',
  },
  connectionBqComputeProjectId: {
    content:
      'Which Big Query project should be used for compute? ' +
      'This is useful if you want to track Kubit costs separately. ',
    actionText: 'Learn More',
    actionUrl:
      'https://cloud.google.com/resource-manager/docs/creating-managing-projects',
  },
  connectionBqJsonKey: {
    content: 'Download the JSON key file for the selected service account.',
  },
  connectionEndDateType: {
    content:
      'The End Date which will be automatically selected in any report when you open up the date widget.',
  },
  dateDt: {
    content:
      'Set the date_dt table name. Use fully qualified table name ' +
      'if it is not part of the default data source database',
  },
} satisfies Record<string, ITooltipOption>;

const nextStepsInfo: ITooltipOption = {
  content: `Streamline your next analysis report by seamlessly
   transferring events, measures, filters, breakdowns, segments, and date ranges.`,
};

const userRoleInfo: ITooltipOption = {
  content: 'Assign individual roles to users',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/kubit-user-roles-and-permissions',
};

const userDefaultSchemaInfo: ITooltipOption = {
  content: 'Automatically assign a default schema to the user upon sign-in.',
};

const userDefaultViewInfo: ITooltipOption = {
  content:
    'Automatically assign a default view (managed or Standard) to the user upon sign-in.',
  actionText: 'Learn More',
  actionUrl: 'https://docs.kubit.ai/docs/dictionary#build-a-managed-view',
};

const explainWithAiRightSideBarTooltips = {
  enabled: {
    content: 'Explain With AI',
  },
};

export {
  // tooltip options
  funnelModeTooltipOption,
  funnelOrderTooltipOption,
  pathGroupByTooltipOption,
  funnelGroupByTooltipOption,
  retentionGroupByTooltipOption,
  pathModeTooltipOption,
  retentionAttributionTooltipOption,
  retentionModeTooltipOption,
  cohortConditionFilterTooltipOption,
  subjectTooltipOption,
  funnelPartitionTooltipOption,
  pathPartitionTooltipOption,
  breakdownOverallTooltipOption,
  dashboardFolderTooltipOption,
  cantDeleteNonEmptyFolderTooltipOption,
  anomalyDetectionInfoTooltipOption,
  brazeRemoveOutdatedTooltipOption,
  saveMeasureTooltipOption,
  defaultPermissionTooltipOption,
  // regular tooltips
  dataTableMeasureInfo,
  funnelStepsInfo,
  funnelOrderTooltips,
  funnelModeTooltips,
  groupbyTooltips,
  attributionTooltips,
  retentionCalculationWindowTooltips,
  retentionCalculationTooltips,
  pathModeTooltips,
  retentionModeTooltips,
  cohortTimeWindowTooltips,
  cohortSampleTooltipOption,
  cohortCompareTooltipOption,
  samplingRateTooltipOption,
  // additional settings
  expressionAdditionalSettingsTooltips,
  funnelAdditionalSettingsTooltips,
  pathAdditionalSettingsTooltips,
  retentionAdditionalSettingsTooltips,
  dataTableAdditionalSettingsTooltips,
  expressionModeTooltip,
  expressionModeOptionTooltips,
  saveNamedFilterTooltip,
  addQueryMeasureTooltip,
  pathEventsTooltip,
  retentionBuilderEventsTooltip,
  retentionBuilderCriteriaTooltip,
  retentionCalculationKeyTooltip,
  pathAdjustmentsTooltip,
  expandReroutingEventTooltip,
  foldReroutingEventTooltip,
  mergeReroutingEventTooltip,
  queryMeasuresInfo,
  queryRollingControlFunctionsInfo,
  queryImpactAnalysisInfo,
  queryHistogramInfo,
  queryComparisonConfigInfo,
  queryFormulaInfo,
  dataModelTooltipOptions,
  dataModelLabelInfoTooltipOptions,
  nextStepsInfo,
  userRoleInfo,
  userDefaultSchemaInfo,
  userDefaultViewInfo,
  explainWithAiRightSideBarTooltips,
};
